import React, { useEffect, useState } from 'react';
import {
  Button, Card, Checkbox, Col, Empty, Input, Row, Select,
  Skeleton, Space, Tag, Tooltip, Typography, message,
} from 'antd';
import { ArrowUpOutlined, CommentOutlined, UserOutlined } from '@ant-design/icons';
import * as api from '../api/client';
import type { FeatureRequestItem } from '../api/client';

const { Text } = Typography;

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  PENDING_REVIEW: 'Pending Review',
  APPROVED_FOR_VOTING: 'Approved for Voting',
  UNDER_CONSIDERATION: 'Under Consideration',
  PLANNED: 'Planned',
  IN_DEVELOPMENT: 'In Development',
  RELEASED: 'Released',
  DECLINED: 'Declined',
  MERGED: 'Merged',
  ARCHIVED: 'Archived',
  DRAFT: 'Draft',
  NEEDS_CLARIFICATION: 'Needs Clarification',
};

const STATUS_TAG_COLORS: Record<string, string> = {
  PENDING_REVIEW: 'orange',
  APPROVED_FOR_VOTING: 'blue',
  UNDER_CONSIDERATION: 'purple',
  PLANNED: 'cyan',
  IN_DEVELOPMENT: 'gold',
  RELEASED: 'green',
  DECLINED: 'red',
  MERGED: 'default',
  ARCHIVED: 'default',
  DRAFT: 'default',
  NEEDS_CLARIFICATION: 'warning',
};

const IMPACT_TAG_COLORS: Record<string, string> = {
  LOW: 'default',
  MEDIUM: 'blue',
  HIGH: 'orange',
  CRITICAL: 'red',
};

const CATEGORY_LABELS: Record<string, string> = {
  AI_AGENTS: 'AI Agents',
  PLAYBOOKS: 'Playbooks',
  KNOWLEDGE_BASE: 'Knowledge Base',
  API: 'API',
  INTEGRATIONS: 'Integrations',
  DESKTOP_APP: 'Desktop App',
  UI_UX: 'UI / UX',
  ADMIN: 'Admin',
  SECURITY: 'Security',
  BILLING: 'Billing',
  OTHER: 'Other',
};

const ALL_STATUSES = [
  'PENDING_REVIEW', 'APPROVED_FOR_VOTING', 'UNDER_CONSIDERATION',
  'PLANNED', 'IN_DEVELOPMENT', 'RELEASED', 'DECLINED', 'MERGED',
];

const ALL_CATEGORIES = [
  'AI_AGENTS', 'PLAYBOOKS', 'KNOWLEDGE_BASE', 'API', 'INTEGRATIONS',
  'DESKTOP_APP', 'UI_UX', 'ADMIN', 'SECURITY', 'BILLING', 'OTHER',
];

const VOTABLE_STATUSES = ['APPROVED_FOR_VOTING', 'UNDER_CONSIDERATION', 'PLANNED', 'IN_DEVELOPMENT'];

const formatDate = (d?: string) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return '—'; }
};

const truncate = (text: string, len: number) =>
  text && text.length > len ? text.slice(0, len) + '…' : text || '';

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  onOpenDetails: (id: string) => void;
  defaultFollowing?: boolean;
}

const FeatureRequestBoard: React.FC<Props> = ({ onOpenDetails, defaultFollowing = false }) => {
  const [items, setItems] = useState<FeatureRequestItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState<'mostVoted' | 'newest' | 'trending' | 'recentlyUpdated'>('mostVoted');
  const [onlyFollowing, setOnlyFollowing] = useState(defaultFollowing);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getFeatureRequests({
        status: status || undefined,
        category: category || undefined,
        sort,
        search: search || undefined,
        following: onlyFollowing || undefined,
      });
      setItems(res.data.items || []);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to load feature requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []); // initial load only

  const handleReset = () => {
    setSearch('');
    setStatus('');
    setCategory('');
    setSort('mostVoted');
    setOnlyFollowing(defaultFollowing);
  };

  // TODO: FeatureRequestItem does not include a hasVoted/currentUserVote field.
  // Once available from the backend, replace the hardcoded `false` with item.hasVoted.
  const toggleVote = async (id: string, currentlyVoted: boolean) => {
    try {
      if (currentlyVoted) {
        await api.removeFeatureRequestVote(id);
      } else {
        await api.voteFeatureRequest(id);
      }
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Vote action failed.');
    }
  };

  // TODO: FeatureRequestItem does not include an isFollowing field.
  // Once available, replace the hardcoded `false` with item.isFollowing.
  const toggleFollow = async (id: string, following: boolean) => {
    try {
      if (following) await api.unfollowFeatureRequest(id);
      else await api.followFeatureRequest(id);
      message.success(following ? 'Unfollowed' : 'Now following');
      await load();
    } catch {
      message.warning('Follow update failed.');
    }
  };

  return (
    <div>
      {/* Filter Bar */}
      <Card className="praxys-feature-filter-card" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} md={6}>
            <Input
              placeholder="Search feature requests"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onPressEnter={() => load()}
              allowClear
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              value={status || undefined}
              placeholder="All Statuses"
              allowClear
              style={{ width: '100%' }}
              onChange={v => setStatus(v || '')}
              options={ALL_STATUSES.map(s => ({ value: s, label: STATUS_LABELS[s] || s }))}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              value={category || undefined}
              placeholder="All Categories"
              allowClear
              style={{ width: '100%' }}
              onChange={v => setCategory(v || '')}
              options={ALL_CATEGORIES.map(c => ({ value: c, label: CATEGORY_LABELS[c] || c }))}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              value={sort}
              style={{ width: '100%' }}
              onChange={v => setSort(v)}
              options={[
                { value: 'mostVoted', label: 'Most Voted' },
                { value: 'trending', label: 'Trending' },
                { value: 'newest', label: 'Newest' },
                { value: 'recentlyUpdated', label: 'Recently Updated' },
              ]}
            />
          </Col>
          <Col xs={12} md={3}>
            <Checkbox
              checked={onlyFollowing}
              onChange={e => setOnlyFollowing(e.target.checked)}
            >
              Following
            </Checkbox>
          </Col>
          <Col xs={24} md={3}>
            <Space>
              <Button type="primary" onClick={load}>Apply</Button>
              <Button onClick={handleReset}>Reset</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Cards */}
      {loading ? (
        <Row gutter={[12, 12]}>
          {[1, 2, 3].map(i => (
            <Col key={i} xs={24} md={12} lg={8}>
              <Card className="praxys-feature-card"><Skeleton active /></Card>
            </Col>
          ))}
        </Row>
      ) : items.length === 0 ? (
        <Empty description="No feature requests found." style={{ margin: '40px 0' }} />
      ) : (
        <Row gutter={[12, 12]}>
          {items.map(item => {
            const canVote = VOTABLE_STATUSES.includes(item.status);
            return (
              <Col key={item.id} xs={24} md={12} lg={8}>
                <Card
                  className="praxys-feature-card"
                  hoverable
                  bodyStyle={{ padding: 14 }}
                >
                  <Row gutter={10} wrap={false}>
                    {/* Vote column */}
                    <Col flex="56px" style={{ textAlign: 'center' }}>
                      <Tooltip
                        title={!canVote ? 'Voting is available after admin approval.' : undefined}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <Button
                            shape="circle"
                            icon={<ArrowUpOutlined />}
                            size="small"
                            disabled={!canVote}
                            onClick={() => toggleVote(item.id, false /* TODO: item.hasVoted */)}
                          />
                          <Text strong style={{ fontSize: 18, lineHeight: '1.4' }}>{item.voteCount}</Text>
                          <Text type="secondary" style={{ fontSize: 10 }}>votes</Text>
                        </div>
                      </Tooltip>
                    </Col>

                    {/* Main content */}
                    <Col flex="auto" style={{ minWidth: 0 }}>
                      <Space direction="vertical" size={6} style={{ width: '100%' }}>
                        <Text
                          strong
                          style={{ fontSize: 14, cursor: 'pointer', display: 'block' }}
                          onClick={() => onOpenDetails(item.id)}
                        >
                          {item.title}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                          {truncate(item.description || item.problemStatement || '', 120)}
                        </Text>
                        <Space size={4} wrap>
                          <Tag color={STATUS_TAG_COLORS[item.status] || 'default'} style={{ margin: 0 }}>
                            {STATUS_LABELS[item.status] || item.status}
                          </Tag>
                          <Tag style={{ margin: 0 }}>
                            {CATEGORY_LABELS[item.category] || item.category}
                          </Tag>
                          <Tag color={IMPACT_TAG_COLORS[item.impactLevel] || 'default'} style={{ margin: 0 }}>
                            {item.impactLevel}
                          </Tag>
                        </Space>
                        <Row justify="space-between" align="middle" wrap={false}>
                          <Space size={10}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <UserOutlined /> {item.createdByName || item.createdByUserId}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <CommentOutlined /> {item.commentCount}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {formatDate(item.updatedOn || item.createdOn)}
                            </Text>
                          </Space>
                          <Space size={4}>
                            <Button size="small" onClick={() => onOpenDetails(item.id)}>
                              Details
                            </Button>
                            <Button
                              size="small"
                              onClick={() => toggleFollow(item.id, false /* TODO: item.isFollowing */)}
                            >
                              Follow
                            </Button>
                          </Space>
                        </Row>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

export default FeatureRequestBoard;
